export interface ThermalPrinterSettings {
  thermal_enabled: boolean;
  bridge_url: string;
  bridge_token: string;
  fallback_browser: boolean;
}

export interface ThermalTicketPayload {
  sale: Record<string, unknown>;
  shop?: Record<string, unknown> | null;
  is_duplicate?: boolean;
  ticket_print_count?: number;
}

export const THERMAL_PRINTER_SETTINGS_KEY = 'suite241_gestion_commerciale_thermal_printer_settings';

export const DEFAULT_THERMAL_PRINTER_SETTINGS: ThermalPrinterSettings = {
  thermal_enabled: false,
  bridge_url: 'http://127.0.0.1:24183',
  bridge_token: 'suite241-local-print-token',
  fallback_browser: true,
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const normalizeBridgeUrl = (url: string): string => {
  const normalized = (url || DEFAULT_THERMAL_PRINTER_SETTINGS.bridge_url).trim();

  return normalized.replace(/\/+$/, '');
};

const isFetchNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();

  return [
    'load failed',
    'failed to fetch',
    'networkerror',
    'network request failed',
  ].some((pattern) => message.includes(pattern));
};

export const loadThermalPrinterSettings = (): ThermalPrinterSettings => {
  if (typeof window === 'undefined') return { ...DEFAULT_THERMAL_PRINTER_SETTINGS };

  try {
    const raw = window.localStorage.getItem(THERMAL_PRINTER_SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};

    return {
      ...DEFAULT_THERMAL_PRINTER_SETTINGS,
      ...parsed,
      thermal_enabled: Boolean(parsed.thermal_enabled),
      fallback_browser: parsed.fallback_browser !== false,
      bridge_url: normalizeBridgeUrl(parsed.bridge_url || DEFAULT_THERMAL_PRINTER_SETTINGS.bridge_url),
      bridge_token: String(parsed.bridge_token || DEFAULT_THERMAL_PRINTER_SETTINGS.bridge_token),
    };
  } catch {
    return { ...DEFAULT_THERMAL_PRINTER_SETTINGS };
  }
};

export const saveThermalPrinterSettings = (settings: ThermalPrinterSettings): ThermalPrinterSettings => {
  const normalized = {
    ...DEFAULT_THERMAL_PRINTER_SETTINGS,
    ...settings,
    bridge_url: normalizeBridgeUrl(settings.bridge_url),
    bridge_token: String(settings.bridge_token || DEFAULT_THERMAL_PRINTER_SETTINGS.bridge_token),
  };

  window.localStorage.setItem(THERMAL_PRINTER_SETTINGS_KEY, JSON.stringify(normalized));

  return normalized;
};

export const saveLocalPrinterBridgeSettings = (
  settings: Pick<ThermalPrinterSettings, 'bridge_url' | 'bridge_token'>
): ThermalPrinterSettings => {
  const normalized = {
    bridge_url: normalizeBridgeUrl(settings.bridge_url),
    bridge_token: String(settings.bridge_token || DEFAULT_THERMAL_PRINTER_SETTINGS.bridge_token),
  };

  window.localStorage.setItem(THERMAL_PRINTER_SETTINGS_KEY, JSON.stringify(normalized));

  return loadThermalPrinterSettings();
};

const bridgeRequest = async (
  path: string,
  settings: ThermalPrinterSettings,
  body?: Record<string, unknown>
): Promise<Record<string, unknown>> => {
  let response: Response;

  try {
    response = await fetch(`${normalizeBridgeUrl(settings.bridge_url)}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Suite241-Print-Token': settings.bridge_token,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    if (isFetchNetworkError(error)) {
      throw new Error(
        "Pont d'impression local indisponible. Vérifiez que Suite241 Print est lancé sur ce poste et que l'URL du pont est correcte."
      );
    }

    throw error;
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok || payload?.ok === false) {
    throw new Error(asString(payload?.message) || `Pont d'impression indisponible (${response.status}).`);
  }

  return payload as Record<string, unknown>;
};

const loadLogoImage = (url: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  const timeout = window.setTimeout(() => reject(new Error('Logo trop long à charger.')), 3500);

  image.crossOrigin = 'anonymous';
  image.onload = () => {
    window.clearTimeout(timeout);
    resolve(image);
  };
  image.onerror = () => {
    window.clearTimeout(timeout);
    reject(new Error('Logo indisponible.'));
  };
  image.src = url;
});

const bytesToBase64 = (bytes: number[]): string => {
  let binary = '';
  const chunkSize = 8192;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.slice(index, index + chunkSize));
  }

  return window.btoa(binary);
};

const buildLogoRasterBase64 = async (shop?: Record<string, unknown> | null): Promise<string | null> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  const logoUrl = asString(shop?.main_logo_url) || asString(shop?.logo_url);
  if (!logoUrl) return null;

  try {
    const image = await loadLogoImage(logoUrl);
    const maxWidth = 384;
    const maxHeight = 180;
    const scale = Math.min(maxWidth / image.naturalWidth, maxHeight / image.naturalHeight, 1);
    const width = Math.max(1, Math.floor(image.naturalWidth * scale));
    const height = Math.max(1, Math.floor(image.naturalHeight * scale));
    const widthBytes = Math.ceil(width / 8);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return null;

    canvas.width = width;
    canvas.height = height;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const pixels = context.getImageData(0, 0, width, height).data;
    const bytes = [
      0x1d, 0x76, 0x30, 0x00,
      widthBytes & 0xff,
      (widthBytes >> 8) & 0xff,
      height & 0xff,
      (height >> 8) & 0xff,
    ];

    for (let y = 0; y < height; y += 1) {
      for (let xByte = 0; xByte < widthBytes; xByte += 1) {
        let value = 0;

        for (let bit = 0; bit < 8; bit += 1) {
          const x = xByte * 8 + bit;
          if (x >= width) continue;

          const offset = (y * width + x) * 4;
          const red = pixels[offset];
          const green = pixels[offset + 1];
          const blue = pixels[offset + 2];
          const alpha = pixels[offset + 3];
          const luminance = (red * 0.299) + (green * 0.587) + (blue * 0.114);

          if (alpha > 24 && luminance < 165) {
            value |= 0x80 >> bit;
          }
        }

        bytes.push(value);
      }
    }

    return bytesToBase64(bytes);
  } catch {
    return null;
  }
};

export const testThermalBridge = (settings: ThermalPrinterSettings): Promise<Record<string, unknown>> => {
  return bridgeRequest('/health', settings);
};

export const printThermalTest = (settings: ThermalPrinterSettings): Promise<Record<string, unknown>> => {
  return bridgeRequest('/print/test', settings, {});
};

export const openThermalDrawer = (settings: ThermalPrinterSettings): Promise<Record<string, unknown>> => {
  return bridgeRequest('/drawer/open', settings, {});
};

export const printThermalTicket = async (
  ticketData: ThermalTicketPayload,
  settings: ThermalPrinterSettings,
  cashierName = ''
): Promise<Record<string, unknown>> => {
  const logoEscposBase64 = await buildLogoRasterBase64(ticketData.shop || null);

  return bridgeRequest('/print/ticket', settings, {
    ...ticketData,
    cashier_name: cashierName,
    logo_escpos_base64: logoEscposBase64,
    paper_width: '80mm',
    open_drawer: true,
  });
};
